import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Badge, Row, Col } from 'react-bootstrap';
import api from '../../utils/api';

const emptyForm = {
  title: '',
  description: '',
  servings: 4,
  prepTime: '',
  cookTime: '',
  totalTime: '',
  equipmentText: '',
  ingredientsText: '',
  instructionsText: '',
  prepAheadNotes: '',
  calories: '',
  protein: '',
  fat: '',
  carbs: '',
  loseItLine: '',
  notes: '',
  tagsText: '',
  isPublished: true,
};

// Ingredients edit as "quantity | item" lines; equipment/tags comma-separated;
// instructions one step per line.
const toForm = (r) => ({
  title: r.title,
  description: r.description || '',
  servings: r.servings || 4,
  prepTime: r.prepTime || '',
  cookTime: r.cookTime || '',
  totalTime: r.totalTime || '',
  equipmentText: (r.equipment || []).join(', '),
  ingredientsText: (r.ingredients || [])
    .map((i) => (i.quantity ? `${i.quantity} | ${i.item}` : i.item))
    .join('\n'),
  instructionsText: (r.instructions || []).join('\n'),
  prepAheadNotes: r.prepAheadNotes || '',
  calories: r.nutrition?.calories ?? '',
  protein: r.nutrition?.protein ?? '',
  fat: r.nutrition?.fat ?? '',
  carbs: r.nutrition?.carbs ?? '',
  loseItLine: r.loseItLine || '',
  notes: r.notes || '',
  tagsText: (r.tags || []).join(', '),
  isPublished: r.isPublished !== false,
});

const toPayload = (f) => ({
  title: f.title,
  description: f.description,
  servings: Number(f.servings) || 4,
  prepTime: f.prepTime,
  cookTime: f.cookTime,
  totalTime: f.totalTime,
  equipment: f.equipmentText.split(',').map((s) => s.trim()).filter(Boolean),
  ingredients: f.ingredientsText
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((line) => {
      const idx = line.indexOf('|');
      if (idx === -1) return { item: line, quantity: '' };
      return { quantity: line.slice(0, idx).trim(), item: line.slice(idx + 1).trim() };
    }),
  instructions: f.instructionsText.split('\n').map((s) => s.trim()).filter(Boolean),
  prepAheadNotes: f.prepAheadNotes,
  nutrition: {
    calories: Number(f.calories) || 0,
    protein: Number(f.protein) || 0,
    fat: Number(f.fat) || 0,
    carbs: Number(f.carbs) || 0,
  },
  loseItLine: f.loseItLine,
  notes: f.notes,
  tags: f.tagsText.split(',').map((s) => s.trim()).filter(Boolean),
  isPublished: f.isPublished,
});

const RecipeManager = () => {
  const [recipes, setRecipes] = useState([]);
  const [show, setShow] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const fetchRecipes = async () => {
    try {
      const { data } = await api.get('/recipes/admin');
      setRecipes(data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchRecipes();
  }, []);

  const handleClose = () => {
    setShow(false);
    setEditingId(null);
    setFormData(emptyForm);
  };

  const handleShow = (recipe = null) => {
    if (recipe) {
      setEditingId(recipe._id);
      setFormData(toForm(recipe));
    }
    setShow(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/recipes/${editingId}`, toPayload(formData));
      } else {
        await api.post('/recipes', toPayload(formData));
      }
      fetchRecipes();
      handleClose();
    } catch (error) {
      console.error(error);
      alert('Error saving recipe');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this recipe?')) {
      try {
        await api.delete(`/recipes/${id}`);
        fetchRecipes();
      } catch (error) {
        console.error(error);
      }
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Recipes</h4>
        <Button onClick={() => handleShow()}>Add Recipe</Button>
      </div>
      <Table striped hover responsive>
        <thead>
          <tr>
            <th>Title</th>
            <th>Tags</th>
            <th>Cal/serv</th>
            <th>Published</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {recipes.map((r) => (
            <tr key={r._id}>
              <td>{r.title}</td>
              <td>
                {(r.tags || []).map((t) => (
                  <Badge bg="secondary" className="me-1" key={t}>{t}</Badge>
                ))}
              </td>
              <td>{r.nutrition?.calories || ''}</td>
              <td>
                <Badge bg={r.isPublished ? 'success' : 'secondary'}>
                  {r.isPublished ? 'Yes' : 'No'}
                </Badge>
              </td>
              <td className="text-end text-nowrap">
                <Button size="sm" variant="outline-primary" className="me-2" onClick={() => handleShow(r)}>
                  Edit
                </Button>
                <Button size="sm" variant="outline-danger" onClick={() => handleDelete(r._id)}>
                  Delete
                </Button>
              </td>
            </tr>
          ))}
          {recipes.length === 0 && (
            <tr>
              <td colSpan="5" className="text-muted text-center">
                No recipes yet. Generate a meal plan or add one manually.
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      <Modal show={show} onHide={handleClose} size="lg">
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{editingId ? 'Edit Recipe' : 'Add Recipe'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control name="title" value={formData.title} onChange={handleChange} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={2} name="description" value={formData.description} onChange={handleChange} />
            </Form.Group>
            <Row>
              <Col sm={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Servings</Form.Label>
                  <Form.Control type="number" name="servings" value={formData.servings} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col sm={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Prep Time</Form.Label>
                  <Form.Control name="prepTime" value={formData.prepTime} onChange={handleChange} placeholder="10 min" />
                </Form.Group>
              </Col>
              <Col sm={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Cook Time</Form.Label>
                  <Form.Control name="cookTime" value={formData.cookTime} onChange={handleChange} placeholder="25 min" />
                </Form.Group>
              </Col>
              <Col sm={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Total Time</Form.Label>
                  <Form.Control name="totalTime" value={formData.totalTime} onChange={handleChange} placeholder="35 min" />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Ingredients (one per line, format: quantity | item)</Form.Label>
              <Form.Control as="textarea" rows={6} name="ingredientsText" value={formData.ingredientsText} onChange={handleChange} placeholder={'2 lbs | Chicken thighs\n1 tbsp | Chili powder'} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Instructions (one step per line)</Form.Label>
              <Form.Control as="textarea" rows={6} name="instructionsText" value={formData.instructionsText} onChange={handleChange} />
            </Form.Group>
            <Row>
              <Col sm={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Equipment (comma-separated)</Form.Label>
                  <Form.Control name="equipmentText" value={formData.equipmentText} onChange={handleChange} placeholder="Oven, Blackstone" />
                </Form.Group>
              </Col>
              <Col sm={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tags (comma-separated)</Form.Label>
                  <Form.Control name="tagsText" value={formData.tagsText} onChange={handleChange} placeholder="chicken, quick, jax-friendly" />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col sm={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Calories</Form.Label>
                  <Form.Control type="number" name="calories" value={formData.calories} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col sm={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Protein (g)</Form.Label>
                  <Form.Control type="number" name="protein" value={formData.protein} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col sm={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Fat (g)</Form.Label>
                  <Form.Control type="number" name="fat" value={formData.fat} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col sm={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Carbs (g)</Form.Label>
                  <Form.Control type="number" name="carbs" value={formData.carbs} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Lose It Entry Line</Form.Label>
              <Form.Control name="loseItLine" value={formData.loseItLine} onChange={handleChange} placeholder="Sheet pan fajitas - 1 serving - 540 cal, 42P, 28F, 24C" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Prep Ahead Notes</Form.Label>
              <Form.Control as="textarea" rows={2} name="prepAheadNotes" value={formData.prepAheadNotes} onChange={handleChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control as="textarea" rows={2} name="notes" value={formData.notes} onChange={handleChange} />
            </Form.Group>
            <Form.Check
              type="switch"
              label="Published (visible on the public page)"
              name="isPublished"
              checked={formData.isPublished}
              onChange={handleChange}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>Cancel</Button>
            <Button type="submit">Save</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default RecipeManager;
